import React, { useState, useEffect, useContext } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardHeader, CardBody, CardFooter, Form, FormGroup, Label, Input, Row, Col, FormFeedback, Alert, InputGroup, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import SessionContext from "SessionContext";
import DualSelect from "partials/dual-select";
import { states as statesList } from "../core/utils/list-of-states";
import { UploadZipFile } from "../core/ui/charts/geo-boundary-widget";
import InfoPopover from "../core/ui/info-popover";
import ConfirmSuccess from "../core/ui/confirm-success";
import ProgressLoader, { LoadingDots } from "partials/progress-loader";
import UnsavedChanges from "partials/unsaved-changes";
import isEqual from 'lodash.isequal';

const _audiences = [
    {
      id: "aud_1",
      label: "Audience 1",
      name: "",
      data: {},
      allow_delete: false,
    },
    {
      id: "aud_2",
      label: "Audience 2",
      name: "",
      data: {},
      allow_delete: false,
    },
];

  /* this is the default audiences - used to check for changes */
  const default_audiences = [
    {
      "id": "aud_1",
      "label": "Audience 1",
      "name": "",
      "data": {
          "label": "Random",
          "value": {},
          "group_type": "RANDOM_SAMPLE"
      },
      "allow_delete": false
    },
    {
      "id": "aud_2",
      "label": "Audience 2",
      "name": "",
      "data": {
          "label": "Random",
          "value": {},
          "group_type": "RANDOM_SAMPLE"
      },
      "allow_delete": false
    }
  ]

let loaded_from_explorer = false;


export default function CompareAudiences({ args: { id, explorer_id } }) {
  const session = useContext(SessionContext);
  const history = useHistory();
  const location = useLocation();
  const max_allowed = 5;
  const company_id = session.appState.activeCompany.id

  const [originalReport, setOriginalReport] = useState({});
  const [preSetAudience, setPreSetAudience] = useState("");
  const [reportName, setReportName] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [allFields, setAllFields] = useState(Object.values(session.chartsStore.comparable));
  const [excludedFields, setExcludedFields] = useState([]);
  const [audiencesList, setAudiencesList] = useState(_audiences);
  const [audiencesCounter, setAudiencesCounter] = useState(3);
  const [maxReached, setMaxReached] = useState(false);
  const [readyToSave, setReadyToSave] = useState(false);
  const [readyToBuild, setReadyToBuild] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuildingReport, setIsBuildingReport] = useState(false);
  const [dropdownMissingValue, setDropdownMissingValue] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const collectAllData = () => {
    let _groups = [];
    let _exclude = [];

    audiencesList.forEach((item, index) => {
      let {value, group_type} = item.data;
      let _is_geo = group_type === "STATES" || group_type === "ZIPS";
      let _temp = {
        "display_index": index,
        "description": item.name,
        "group_type": group_type,
        "geo_selection": _is_geo ? value : [],
        "reference_id": !_is_geo ? Object.keys(value)[0] : null,
      }
      _groups.push(_temp);
    })

    excludedFields.length && excludedFields.forEach((val) => {
      _exclude.push({ds: val.ds, id: val.id})
    })

    let _data = {
      "comparison_report": {
        "am_company_id": company_id,
        "name": reportName,
        "description": reportDescription,
        "comparison_variables": _exclude,
      },
      "comparison_groups": _groups,
    }
    return _data;
  }

  const handleSave = (update=false) => {
    let save_data = collectAllData();
    let api_url = `compare/save`;
    
    if(update) {
      save_data.comparison_report.id = originalReport.comparison_report.id;
      api_url = `compare/update`;
    } 

    session.command(api_url, save_data, {}, { defaultErrorHandling: false })
    .then(() => {
      setShowSuccess(true);
      setHasUnsavedChanges(false);
      setTimeout(() => {
        setShowSuccess(false);
        history.push('/compare-list');
      }, 2000);
    })
    .catch((error) => {
      console.log("Failed to Save Report", JSON.stringify(error));
    })

  }

  const handleBuild = () => {
    let _group = [];
    let _all_data = collectAllData();
    setIsBuildingReport(true);

    if(originalReport?.comparison_report) {
      _all_data.comparison_report.id = originalReport.comparison_report.id;
    } 
    
    audiencesList.forEach((item) => {
      let _value = item.data.value;
      let _label = item.data.label;

      if (Array.isArray(_value)) {
        // states
        if(_label === "State") {
          const full_names = _value.map(code => {
            const found = statesList.find(state => state.code === code);
            return found ? found.name : code; 
          });
          _value = full_names;
        } 
      } else if (_value && typeof _value === 'object') {
        // random
        if(_label === "Random") {
          _value = ["Random Selection"];
        } else {
          // dropdown selections
          let _option = Object.values(_value);
          _value = _option || [""];
        }
      } else {
        // _value is something else (string, number, null, etc.)
        _value = [""];
      }

      let _temp = { 
        name: item.name,
        label: _label,
        values: _value,
      };
      _group.push(_temp);
    })

    let _user = originalReport?.comparison_report ? originalReport.comparison_report.created_by : session.appState.identity.user.name;

    let selection_criteria = {
      report_name: reportName,
      report_description: reportDescription,
      created_by: _user,
      variables: excludedFields,
      groups: _group
    }

    _all_data.comparison_report.saved_selection_criteria = selection_criteria;

    session.command("compare/build-report", _all_data, {}, { defaultErrorHandling: false })
    .then(() => {
      setShowSuccess(true);
      setHasUnsavedChanges(false);
      setTimeout(() => {
        setShowSuccess(false);
        history.push('/compare-list');
      }, 2000);
    })
    .catch((error) => {
      setIsBuildingReport(false);
      console.log("Failed to Build Report", JSON.stringify(error));
    })

  }

  const handleNameChange = (e) => {
    setReportName(e.target.value);
  }

  const handleDescriptionChange = (e) => {
    setReportDescription(e.target.value);
  }

  const handleUpdateAudiences = (item) => {
    setAudiencesList(prev =>
      prev.map(aud =>
        aud.id === item.id ? { ...aud, ...item } : aud
      )
    );
  }

  const handleAddNewAudience = () => {
    if(audiencesCounter > max_allowed) {
      return;
    }
    setAudiencesCounter(audiencesCounter + 1);
    let _updated_list = [...audiencesList, {
      id: crypto.randomUUID(),
      label: `Audience ${audiencesList.length + 1}`,
      name: "",
      data: {},
      allow_delete: true,
    }];
    setAudiencesList(_updated_list);
  }

  const handleDeleteAudience = (_id) => {
    setAudiencesList(prev => {
      let _temp = prev
        .filter(aud => aud.id !== _id)
        .map((item, index) => {
          const newLabel = item.label.match(/\d+$/)
            ? item.label.replace(/\d+$/, index + 1)
            : `${item.label} ${index + 1}`;

          return { ...item, label: newLabel }; // create a new object
        });
      return _temp;
    });

    setAudiencesCounter(audiencesList.length);
  }

  useEffect(() => {
    if(!session.chartsStore.loaded) {
      return;
    }
    const initialDataLoad = () => {
      if(id) {
        session.query(`compare/get/${id}`, {})
        .then((report) => {
          setOriginalReport(report);
          return report;
        })
        .then((res) => {
          setReportName(res.comparison_report.name);
          setReportDescription(res.comparison_report.description);
          // add drag and drop field
          const updatedVariables = res.comparison_report.comparison_variables.map(v => {
            const match = session.chartsStore.comparable.find(f => f.id === v.id);
            return match ? { ...v, ...match } : v;
          });
          setExcludedFields(updatedVariables);
          // add the groups needed
          setAudiencesList([]);
          let _updated_list = [];
          res.comparison_groups.forEach((val, index) => {
            _updated_list = [..._updated_list, {
              id: crypto.randomUUID(),
              label: `Audience ${_updated_list.length + 1}`,
              name: "",
              data: {},
              allow_delete: index > 1 ? true : false,
            }];
          })
          setTimeout(() => {
            setAudiencesList(_updated_list);
            setAudiencesCounter(res.comparison_groups.length + 1);
          }, 200);        
        })
        .catch((error) => console.log("Failed to get audience.", error))
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        })

      } else if(explorer_id) {
        session.query("audience/get", { id: explorer_id })
        .then((audience) => {
          setPreSetAudience(audience.name);
        })
        .then(() => {
          setIsLoading(false);
          setHasUnsavedChanges(false);
          setTimeout(() => {
            history.replace({
              pathname: location.pathname,
              search: location.search,
            });
          }, 600);
        })
        .catch((error) => console.log("Failed to get audience.", error))
      } else {
        setIsLoading(false);
      }
    }

    initialDataLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explorer_id, id, session.chartsStore.loaded]);

  useEffect(() => {
    setMaxReached(audiencesCounter > max_allowed ? true : false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiencesCounter]);

  useEffect(() => {
    
    let is_missing_audience_name = false;
    if(audiencesList && audiencesList.length) {
      audiencesList.forEach((val) => {
        if(!val.name.length) {
          is_missing_audience_name = true;
        }
      })
    }
    
    /* check it has name, description, variables, and audience name */
    let has_name_description = (reportName.length && reportDescription.length);
    if(!has_name_description || dropdownMissingValue || !excludedFields.length || is_missing_audience_name) {
      setReadyToSave(false);
      setReadyToBuild(false);
    } else if(id) {
      if(audiencesList && audiencesList.length && originalReport && !isBuildingReport) {
        setReadyToBuild(true);
        let _original = originalReport.comparison_report;
        const excludedIds = excludedFields.map(f => f.id).sort();
        const comparisonIds = _original.comparison_variables.map(f => f.id).sort();

        // normalize both arrays to a comparable structure:
        const normalizeStored = (arr) => {
          let _temp = [];
          arr.forEach(item => {
            let _type = item.group_type;
            let _value = (_type === "STATES" || _type === "ZIPS") ? 
              item.geo_selection : item.reference_id;
            _temp.push({
              description: item.description,
              group_type: item.group_type,
              value: _value || "random",
            });
          });
          return _temp;
        }
        const normalizeCurrent = (arr) => {
          let _temp = [];
          arr.forEach(item => {
            let _type = item.data.group_type;
            let _value = (_type === "STATES" || _type === "ZIPS") ?
              item.data.value :
              Object.keys(item.data.value || {})[0];
            _temp.push({
              description: item.name,
              group_type: item.data.group_type,
              value: _value || "random",
            });
          });
          return _temp;
        }
          
        // compare deep equality
        const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

        let areEqual = true;

        // only compare if the saved has values
        if(comparisonIds.length) {
          areEqual = isEqual(normalizeStored(originalReport.comparison_groups), normalizeCurrent(audiencesList));
        }

        if(
          _original.name !== reportName ||
          _original.description !== reportDescription ||
          ( JSON.stringify(excludedIds) !== JSON.stringify(comparisonIds) ) ||
          !areEqual
        ) {
          setReadyToSave(true);
        } else {
          setReadyToSave(false);
        }
      } 
    } else {
      setReadyToSave(true);
      setReadyToBuild(true);
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportName, reportDescription, originalReport, audiencesList, excludedFields])

  useEffect(() => {
    // the fields to prioritize, in given order
    const _top_priority = [
      "v-epsln-demog-000029", "v-epsln-demog-000027", "v-epsln-demog-000233", "v-epsln-demog-000079",
      "v-epsln-demog-000042", "v-epsln-demog-000028", "v-epsln-demog-000006", "v-epsln-demog-000006",
      "v-epsln-demog-000002", "v-epsln-demog-000004", "v-epsln-demog-000010"
    ];

    // remove these fields from the options list
    const remove_these = [ "email_deployment", "digital_onboarding_1", "digital_onboarding_2", "digital_onboarding_3", "prev_addr1", "prev_city", "prev_state", "prev_zip", "prev_zip4", "v-epsln-demog-000775"];

    const sortByPriority = (list, priorityIds) => {
      const prioritySet = new Set(priorityIds);
      
      // separate items into prioritized and the rest
      const prioritized = [];
      const others = [];

      for (const item of list) {
        if (prioritySet.has(item.id)) {
          prioritized.push(item);
        } else {
          others.push(item);
        }
      }

      // sort prioritized items
      prioritized.sort(
        (a, b) => priorityIds.indexOf(a.id) - priorityIds.indexOf(b.id)
      );

      return [...prioritized, ...others];
    };

    const sortedList = sortByPriority(session.chartsStore.comparable, _top_priority);

    const filtered_list = sortedList.filter(item => !remove_these.includes(item.id));

    setAllFields(filtered_list);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedFields])

  useEffect(() => {
    if(isLoading) { return }
    let timeoutId;
    if(readyToSave) {
      setHasUnsavedChanges(true);
    } else if(Object.keys(originalReport).length === 0) {
      // when creating a new report: check if any field has data
      timeoutId = setTimeout(() => {
        if(
          reportName.length ||
          reportDescription.length ||
          excludedFields.length ||
          !isEqual(default_audiences, audiencesList)
        ) {
          setHasUnsavedChanges(true);
        } else {
          setHasUnsavedChanges(false);
        }
      }, 800);
    } else {
      setHasUnsavedChanges(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  // eslint-disable-next-line
  }, [readyToSave, originalReport, isLoading, reportName, reportDescription, audiencesList, excludedFields])

  useEffect(() => {
    // reset the flag when leaving the page
    return () => {
      loaded_from_explorer = false;
    }
  }, [])


  if(isLoading) {
    return (
      <div className="loader-container">
        <ProgressLoader />
      </div>
    );
  }

  if(isBuildingReport) {
    return (
      <div className="page admin-page">
        <div className="m-5 p-3">
          <LoadingDots message="Building report, please wait..." />
          
        </div>
        <ConfirmSuccess show={showSuccess} label="Report built successfully." />
      </div>
    );
  }


  return (
    <div className="page admin-page">
      <div className="list-header sticky-page-header">
        <Link to="/compare-list">
          <Button type="button" className="new-button">
            <i className="bi bi-chevron-left"></i> Compare
          </Button>
        </Link>
        <h1>Compare Audiences</h1>
        <i className="spacer" />
        <div className="model-actions header-actions">
          <Button
            className="new-button promote"
            type="button"
            disabled={!readyToSave}
            onClick={() => handleSave(id ? true : false)}>
            {id ? "Update" : "Save"}
          </Button>
          <Button
            className="new-button promote"
            type="button"
            disabled={!readyToBuild}
            onClick={handleBuild}>
            Build
          </Button>
        </div>
      </div>
      <div className="roles-main margin-top-1">
        <div className="m-3">
          <Card className="w-100 m-1">
            <CardHeader>                
              <Form className='bs-form model-form model-name-desc' onSubmit={e => e.preventDefault()}>
                <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="model-name">Comparison Report Name:</Label>
                        <Input
                          id="model-name"
                          type="text"
                          onChange={handleNameChange}
                          value={reportName}
                          invalid={!reportName}
                          maxLength={108}
                        />
                        <FormFeedback>
                          *Report Name Required.
                        </FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={8}>
                      <FormGroup>
                        <Label for="model-description">Description:</Label>
                        <Input
                          type="textarea"
                          rows="1"
                          onChange={handleDescriptionChange}
                          value={reportDescription}
                          invalid={!reportDescription}
                          maxLength={108}
                        />
                        <FormFeedback>
                          *Description Required.
                        </FormFeedback>
                    </FormGroup>
                    </Col>
                </Row>
              </Form>
            </CardHeader>
            <CardBody>
              {audiencesList.map((item, i) => {
                if(i > audiencesCounter) {
                  return null;
                }
                return (
                  <AudienceEditor 
                    details={item} 
                    key={item.id} 
                    onUpdateAudience={handleUpdateAudiences}
                    handleDelete={handleDeleteAudience}
                    preSetAudience={preSetAudience}
                    original={originalReport}
                    rowIndex={i}
                    setDropdownMissingValue={setDropdownMissingValue}
                  />
                );
              })}
            </CardBody>
            <CardFooter className="d-flex justify-content-end">
              <Button type="button" className="new-button" onClick={handleAddNewAudience} disabled={maxReached}>
                {!maxReached ? (<><i className="bi bi-plus"></i> Add Additional Audience</>) : "Maximum Amount Reached"}
              </Button>
            </CardFooter>            
          </Card>
          <div className="mt-5">
            <Card className="w-100 m-1">
              <CardHeader>
                <Form className='bs-form model-form model-name-desc' onSubmit={e => e.preventDefault()}>
                  <label className="form-label">
                    Variables for Comparison
                    <span className="ms-2 fs-6">
                      <InfoPopover
                        title="Variables for Comparison"
                        message="Select the variables you want to compare."
                        offsetTop={0}
                      />
                    </span>
                  </label>
                  <Input
                    type="text"
                    rows="1"
                    onChange={() => {}}
                    value={excludedFields}
                    invalid={!excludedFields.length}
                    maxLength={1}
                    className="d-none"
                  />
                  <FormFeedback>
                    *Minimum One Variable Required.
                  </FormFeedback>
                </Form>
              </CardHeader> 
              <CardBody>
                <DualSelect
                  optionLabel='label'
                  allOptions={allFields}
                  selectedOptions={excludedFields}
                  setSelectedOptions={setExcludedFields}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <ConfirmSuccess show={showSuccess} label="Report saved successfully." />
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
    </div>
  );
}


/* add more audiences to compare */
const AudienceEditor = ({details, onUpdateAudience, handleDelete, preSetAudience, original, rowIndex, setDropdownMissingValue}) => {
  const session = useContext(SessionContext);
  const {label, id, name, allow_delete} = details;
  const selection_options = [
    "Random Sample of Total United States",
    "My Uploads",
    "My Saved Audiences",
    "My Prior Orders",
    "Zip Code",
    "State"
  ];

  const [updatedName, setUpdatedName] = useState(name || "");
  const [selected, setSelected] = useState(selection_options[0]);
  const [uploads, setUploads] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [orders, setOrders] = useState([]);
  const [states, setStates] = useState({});
  const [zipcodes, setZipcodes] = useState({});
  const [finalData, setFinalData] = useState({});
  const [isFirstGroup, setIsFirstGroup] = useState(false);

  const [editingUploads, setEditingUploads] = useState();
  const [editingAudiences, setEditingAudiences] = useState();
  const [editingOrders, setEditingOrders] = useState();
  const [editingStates, setEditingStates] = useState();
  const [editingZipcodes, setEditingZipcodes] = useState();
  const [isLoadingEditor, setIsLoadingEditor] = useState(true);

  const handleAudienceNameChange = (e) => {
    setUpdatedName(e.target.value);
  }

  const updateAudience = () => {
    let _details = {
      id: id,
      label: label,
      name: updatedName,
      data: finalData,
      allow_delete: allow_delete
    };
    onUpdateAudience(_details);
  }

  const onClickDelete = () => {
    handleDelete(id);
  }

  const handleAudienceSelections = (val) => {
    setFinalData(val);
  }

  const handleUploadsSelections = (val) => {
    setFinalData(val);
  }

  const handleRadioSelection = (val) => {
    setSelected(val);
  }

  const selection_dictionary = {
    RANDOM_SAMPLE: "Random Sample of Total United States",
    AUDIENCE: "My Saved Audiences",
    UPLOAD: "My Uploads",
    ORDER: "My Prior Orders",
    ZIPS: "Zip Code",
    STATES: "State"
  }

  const hasValues = value =>
    Array.isArray(value)
      ? value.length > 0
      : value && typeof value === 'object' && Object.keys(value).length > 0;

  useEffect(() => {
    if(!selected) {return}

    if(selected === "Random Sample of Total United States") {
      setDropdownMissingValue(false);
      return;
    }

    if(!finalData.group_type) {return}
    
    if(selected !== selection_dictionary[finalData.group_type]) {
      setDropdownMissingValue(true);
    } else {
      // it has change the radio option: also check if a value was selected
      if(hasValues(finalData.value)) {
        setDropdownMissingValue(false);
      } else {
        setDropdownMissingValue(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, finalData])

  useEffect(() => {
    updateAudience();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, updatedName, finalData])

  useEffect(() => {
    updateAudience();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(!selected) {
      handleRadioSelection("Random Sample of Total United States");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details])

  const getValues = (_charts, _choice) => {
    if(!_charts.length) {return}
    let _types = {uploads: "UPLOAD", audiences: "AUDIENCE", orders: "ORDER"};
    let _temp_data = [];
    _charts.forEach((item) => {
      const config = session.chartsStore.charts[item];
      if(!config) {return}
      let _builder = {
        label: config.label,
        value: config.value_labels || {},
        group_type: _types[_choice],
      };
      _temp_data.push(_builder);
    })

    setTimeout(() => {
      if(_choice === "uploads") {
        setUploads(_temp_data);
      } else if(_choice === "audiences") {
        setAudiences(_temp_data);
      } else if(_choice === "orders") {
        setOrders(_temp_data);
      }
    }, 200);
  }

  useEffect(() => {
    if(!session.chartsStore.loaded || !session.chartsStore.taxonomy[1]["My Data"]) {
      return;
    }
    setIsLoadingEditor(true);
    let _charts = session.chartsStore.taxonomy[1]["My Data"][1];
    let _uploads = _charts["Uploads"]?.[0] ?? [];
    let _saved_audiences = _charts["Saved Audiences"]?.[0] ?? [];
    let _prior_orders = _charts["Prior Orders"]?.[0] ?? [];

    getValues(_uploads, "uploads");
    getValues(_saved_audiences, "audiences");
    getValues(_prior_orders, "orders");

    let prefix = session.crossfilterStore.geoPrefix;

    let _states = `${prefix}state`;
    let _zip_codes = `${prefix}zip`;
    
    let states_info = session.chartsStore.charts[_states];
    let zipcodes_info = session.chartsStore.charts[_zip_codes];

    if(!zipcodes_info || !states_info) {
      window.location.reload();
      return;
    }

    let zip_builder = {
      label: zipcodes_info.label,
      value: [],
      group_type: "ZIPS",
    };

    let states_builder = {
      label: states_info.label,
      value: statesList ? statesList.reduce((acc, item) => {
          acc[item.code] = item.name;
          return acc;
        }, {})
      : {},
      group_type: "STATES",
    };

    setStates(states_builder);
    setZipcodes(zip_builder);
    setIsLoadingEditor(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.chartsStore.loaded, session.chartsStore.taxonomy])

  useEffect(() => {
    if(preSetAudience.length && (label === "Audience 1")) {
      setSelected(selection_options[2]);
      setIsFirstGroup(true);
    } else {
      setIsFirstGroup(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSetAudience, label])

  useEffect(() => {
    if(original && original.comparison_groups) {
      const selection_options_enum = {
        "RANDOM_SAMPLE": "Random Sample of Total United States",
        "UPLOAD": "My Uploads",
        "AUDIENCE": "My Saved Audiences",
        "ORDER": "My Prior Orders",
        "ZIPS": "Zip Code",
        "STATES": "State"
      };
      let _row = original.comparison_groups[rowIndex];
      setUpdatedName(_row?.description || "");
      setSelected(selection_options_enum[_row?.group_type || ""]);

      if(_row && _row.group_type) {
        if(_row.group_type === "ZIPS") {
          setEditingZipcodes(_row.geo_selection);
        } else if(_row.group_type === "STATES") {
          setEditingStates(_row.geo_selection);
        } else if(_row.group_type === "UPLOAD") {
          setEditingUploads(_row.reference_id);
        } else if(_row.group_type === "AUDIENCE") {
          setEditingAudiences(_row.reference_id);
        } else if(_row.group_type === "ORDER") {
          setEditingOrders(_row.reference_id);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if(isLoadingEditor) {
    return (
      <div className="m-5 p-3">
        <LoadingDots message="Loading data..." />
      </div>
    );
  }

  return (
    <Card className="w-100 audience-compare-wrapper">
      <CardHeader>
        <span>
          <strong>{label}</strong>
            <span className="ms-2 fs-6">
              <InfoPopover
                title="Audience Details"
                message="Select the details to include in your Audience Comparison Report."
                offsetTop={0}
              />
            </span>
          </span>
        {allow_delete && <i className="bi bi-trash3 audience-delete" title="Remove Audience" onClick={onClickDelete} />}
      </CardHeader> 
      <CardBody>
        <div className="field-label">
          Name:
          <span className="ms-2 fs-6">
            <InfoPopover
              title="Name this Audience"
              message="Define how your audience will be named in the Audience Comparison Report."
              offsetTop={0}
            />
          </span>
        </div>
        <Input
          type="text"
          onChange={handleAudienceNameChange}
          value={updatedName}
          className="name-input"
          placeholder="Name this Audience..."
          maxLength={100}
          invalid={!updatedName}
        />
        <FormFeedback>
          *Audience Name Required.
        </FormFeedback>

        <div className="field-label mt-3 mb-3">Select an Option:</div>

        <div className="options-wrapper">
          <div className="options-left">
            <div className="radio-wrapper">
              {selection_options.length ? selection_options.map((option) => (
                <div key={option}>
                  <div className="option-radio" onClick={() => handleRadioSelection(option)}>
                    {selected === option ? (
                      <span className="radio-base radio-selected" />) : (<span className="radio-base radio-not-selected" />
                    )}
                    <span className="option-label">{option}&nbsp;</span>
                  </div>
                </div>
              )) : ""}
            </div>
          </div>
          <div className="options-right">
            {selected === "Random Sample of Total United States" && <RandomSelected 
              updateSelection={handleAudienceSelections} setDropdownMissingValue={setDropdownMissingValue} />}

            {selected === "My Uploads" && <MyUploadsSelected 
              options={uploads} 
              updateSelection={handleUploadsSelections} 
              setDropdownMissingValue={setDropdownMissingValue}
              editing={editingUploads} />}

            {selected === "My Saved Audiences" && (
              <MyAudiencesSelected 
                preSet={isFirstGroup ? preSetAudience : ""} 
                options={audiences} 
                updateSelection={handleAudienceSelections}
                editing={editingAudiences}
                setDropdownMissingValue={setDropdownMissingValue}
              />)}

            {selected === "My Prior Orders" && <MyOrdersSelected 
              options={orders} 
              updateSelection={handleAudienceSelections} 
              setDropdownMissingValue={setDropdownMissingValue}
              editing={editingOrders} />}

            {selected === "Zip Code" && <ZipCodeSelected options={zipcodes} 
              updateSelection={handleAudienceSelections} 
              setDropdownMissingValue={setDropdownMissingValue}
              editing={editingZipcodes} />}

            {selected === "State" && <StatesSelected 
              options={states} 
              updateSelection={handleAudienceSelections} 
              setDropdownMissingValue={setDropdownMissingValue}
              editing={editingStates} />}
          </div>
        </div>

      </CardBody>
    </Card>
  )
}

const RandomSelected = ({updateSelection, setDropdownMissingValue}) => {
  useEffect(() => {
    let _selected = {
      label: "Random",
      value: {},
      group_type: "RANDOM_SAMPLE"
    }
    updateSelection(_selected);
    setDropdownMissingValue(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div>
      <h6>Random Sample</h6>
      <Alert color="warning" className="d-flex">
        <i className="bi bi-lightbulb"></i>
        <div>
          This dataset includes a random sample of 2 million records from across the United States.
        </div>
      </Alert>
    </div>
  )
}

const MyUploadsSelected = ({options, updateSelection, editing, setDropdownMissingValue}) => {
  const [selectedMenuItem, setSelectedMenuItem] = useState();

  const handleSelectItem = (item) => {
    setSelectedMenuItem(item);
  }

  const handleSelectDropdown = (item) => {
    updateSelection(item);
  }

  useEffect(() => {
    if(editing && options && options.length) {
      const found = options.find(item => editing in item.value);
      handleSelectItem(found);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, options])

  if(!options.length) {
    return (
      <div>
        <h6>No Uploads Available</h6>
      </div>
    )
  }

  return (<>
    <div className="upload-sub-menu">
      {options.map((item, index) => (
        <div key={item.label + index}>
          <Button
            className={selectedMenuItem?.label === item.label ? "min-width promote" : "min-width"}
            onClick={() => handleSelectItem(item)}>
            {item.label}
          </Button>
        </div>
      ))}
    </div>
    <div className="right-wrapper">
      {selectedMenuItem ? (<>
        <h6>{selectedMenuItem.label}</h6>
        <SearchableDropDown
          options={selectedMenuItem}
          handleSelection={handleSelectDropdown}
          editing={editing}
          setDropdownMissingValue={setDropdownMissingValue}
        />
      </>) : ""}
    </div>
  </>)
}

const MyAudiencesSelected = ({options, updateSelection, preSet, editing, setDropdownMissingValue}) => {

  const [preSetValue, setPreSetValue] = useState({});

  const handleSelectDropdown = (item) => {
    updateSelection(item);
  }

  useEffect(() => {
    if(preSet.length && !loaded_from_explorer && options && options[0]) {
      const find_audience = Object.keys(options[0].value).find(
        key => options[0].value[key] === preSet
      );
      const result = find_audience ? { [find_audience]: options[0].value[find_audience] } : null;
      setPreSetValue(result);
      loaded_from_explorer = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSet, options])

  if(!options.length) {
    return (
      <div>
        <h6>No Saved Audiences Available</h6>
      </div>
    )
  }


  return (
    <div>
      <h6>Saved Audiences</h6>
      <SearchableDropDown
        options={options[0]}
        handleSelection={handleSelectDropdown}
        preSet={preSetValue}
        editing={editing}
        setDropdownMissingValue={setDropdownMissingValue}
      />
    </div>
  )
}

const MyOrdersSelected = ({options, updateSelection, editing, setDropdownMissingValue}) => {

  const handleSelectDropdown = (item) => {
    updateSelection(item);
  }

  if(!options.length) {
    return (
      <div>
        <h6>No Prior Orders Available</h6>
      </div>
    )
  }

  return (
    <div>
      <h6>Prior Orders</h6>
      <SearchableDropDown
        options={options[0]}
        handleSelection={handleSelectDropdown}
        editing={editing}
        setDropdownMissingValue={setDropdownMissingValue}
      />
    </div>
  )
}

const ZipCodeSelected = ({options, updateSelection, editing, setDropdownMissingValue}) => {

  const [zipSearch, setZipSearch] = useState("");
  const [zipCodes, setZipCodes] = useState([]);

  const addNewZipCodes = (new_zips) => {
    let _temp = zipCodes.concat(new_zips);
    const uniqueZipCodes = [...new Set(_temp)];
    setZipCodes(uniqueZipCodes);
  }

  const onUploadFile = (e) => {
    let zipList = e.split(",");
    addNewZipCodes(zipList);
  }

  const onSearchPaste = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const entries = ev.clipboardData.getData('text/plain').match(/[0-9]{5}/g);
    addNewZipCodes(entries);
  }

  const onSearchChange = (ev) => {
    setZipSearch(ev.target.value);
  }

  const onKeyUp = (ev) => {
    if (ev.which === 13 && zipSearch.length === 5) {
      addNewZipCodes(zipSearch);
      setZipSearch("");
    }
  }

  const handleEnterButton = (e) => {
    if(zipSearch.length === 5) {
      addNewZipCodes(zipSearch);
      setZipSearch("");
    }
  }

  const handleRemoveZip = (val) => {
    const updated = zipCodes.filter(zip => zip !== val);
    setZipCodes(updated);
  }

  useEffect(() => {
    let _values = {...options, "value": zipCodes}
    updateSelection(_values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCodes])

  useEffect(() => {
    if(editing) {
      addNewZipCodes(editing);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  return (<>
    <div className="upload-sub-menu">
      <h6>Zip Code</h6>
      <div className="left-wrapper">
        <div className="input-wrapper">
          <Input type='search' placeholder='Enter or Paste Values' 
            onKeyUp={onKeyUp}
            value={zipSearch} 
            onChange={onSearchChange}
            onPaste={onSearchPaste}
            className="mb-2 zip-input"
          />
          <div className="click-enter" onClick={handleEnterButton}>
            <i className="fa-solid fa-circle-up"></i>
          </div>
        </div>
        <UploadZipFile className="zip-input" fileContentHandler={onUploadFile} />
      </div>
    </div>
    <div className="right-wrapper">
      <div className="show-selections">
        {zipCodes.map((zip, i) => (
          <div className="each-selection" key={i}>
            {zip} <i className="fa-regular fa-circle-xmark" onClick={() => handleRemoveZip(zip)}></i>
          </div>
        ))}
      </div>
    </div>
  </>)
}

const StatesSelected = ({options, updateSelection, editing}) => {

  const [selected, setSelected] = useState([]);

  const handleSelectDropdown = (item) => {
    let _temp = [...selected, item.value];
    // removes any duplicates
    const unique = Array.from(
      new Map(_temp.map(obj => [Object.keys(obj)[0], obj])).values()
    );
    setSelected(unique);
  }

  const handleRemoveState = (val) => {
    const updated = selected.filter(st => st !== val);
    setSelected(updated);
  }

  useEffect(() => {
    let keys = selected.map(obj => Object.keys(obj)[0]);
    let _values = {...options, "value": keys};
    updateSelection(_values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  useEffect(() => {
    if(editing) {
      const result = editing
        .filter(code => options.value[code])
        .map(code => ({ [code]: options.value[code] }));
      setSelected(result);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  return (<>
    <div className="upload-sub-menu">
      <h6>State</h6>
      <div className="left-wrapper">
        <SearchableDropDown
          options={options}
          handleSelection={handleSelectDropdown}
          showSelection={false}
        />
      </div>
    </div>
    <div className="right-wrapper">
      <div className="show-selections">
        {selected.map((_state, i) => (
          <div className="each-selection each-state" key={i}>
            {Object.values(_state)[0]} <i className="fa-regular fa-circle-xmark" onClick={() => handleRemoveState(_state)}></i>
          </div>
        ))}
      </div>
    </div>
  </>)
}


const SearchableDropDown = ({ options, handleSelection, showSelection=true, preSet, editing, setDropdownMissingValue }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [dropDownIsOpen, setDropDownIsOpen] = useState(false);

  const toggleDropDown = () => setDropDownIsOpen((prev) => !prev);

  const onSelect = (e, _key, _value) => {
    e?.preventDefault();
    let _available_values = Object.values(options.value);
    let _find_selection = _available_values.includes(_value);
    let _display_selection = _find_selection ? _value : "The item you previously selected is no longer available. Please choose a new one to continue.";
    setSelectedName(_display_selection);
    let _selected = {
      label: options.label,
      value: {[_key]: _value},
      group_type: options.group_type,
    }
    _display_selection && handleSelection(_selected);
    setTimeout(() => setDropDownIsOpen(false), 100);
  };

  const filteredOptions = options?.value
    ? Object.entries(options.value).filter(([key, value]) =>
        value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  useEffect(() => {
    if(preSet && Object.keys(preSet).length) {
      let _key = Object.keys(preSet);
      let _value = Object.values(preSet);
      onSelect(null, _key[0], _value[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSet, options])

  useEffect(() => {
    if(editing && options) {
      onSelect(null, editing, options.value[editing]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, options])

  useEffect(() => {
    if(setDropdownMissingValue) {
      let has_selection = selectedName?.length ? true : false;
      setDropdownMissingValue(!has_selection);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName, setDropdownMissingValue])


  return (<>
    <div className="drop-down">
      <InputGroup>
        <Input
          type="search"
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
        />
        <ButtonDropdown toggle={toggleDropDown} isOpen={dropDownIsOpen}>
          <DropdownToggle caret>
            { options && options.label === "State" ? "Select State" : "Select file" }
          </DropdownToggle>
          <DropdownMenu>
            {filteredOptions.map(([key, value]) => (
              <DropdownItem key={key} value={value} onClick={(e) => onSelect(e, key, value)}>
                {value}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </ButtonDropdown>
      </InputGroup>
    </div>

    {(selectedName && showSelection) && (
      <div className="mt-4">
        <h6>Selected</h6>
        <div>{selectedName}</div>
      </div>
    )}
    
  </>);
};


